import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonLambda = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Anonymous Logic: Lambda Functions</h1>
                <p className="subtitle">Mastering the one-line function for cleaner, functional code.</p>
            </div>

            <div className="content-section">
                <h2>1. What is a Lambda?</h2>
                <ProTip><p><strong>Definition:</strong> A lambda function is a small, anonymous function defined without a name.</p></ProTip>
                <p>
                    In the world of computer science, the term "Lambda" isn't random. It comes from <strong>Lambda Calculus</strong>,
                    a formal system in mathematical logic introduced by Alonzo Church in the 1930s. Church wanted a way to formalize
                    mathematics using functions.
                </p>
                <p>
                    In Python, this mathematical purity is preserved: a lambda is a function that is purely about <strong>transformation</strong>
                    (taking an input and returning an output) without the "side effects" or "statements" that clutter normal imperative programming.
                </p>
                <p>
                    While normal functions are defined using the `def` keyword and can be complex, lambda functions use the `lambda` keyword
                    and are designed to be short, sweet, and typically used only once.
                </p>

                <h3>The Anatomy of Anonymous</h3>
                <p>
                    The syntax `lambda arguments: expression` captures the essence of functional programming:
                </p>
                <ul>
                    <li><strong>No Name:</strong> It doesn't pollute your namespace.</li>
                    <li><strong>Single Expression:</strong> It forces you to think in terms of return values, not steps.</li>
                    <li><strong>First-Class Citizen:</strong> It can be passed around just like a number or a string.</li>
                </ul>


                <h3>The Expression Constraint</h3>
                <p>
                    This is the most confusing part for beginners coming from JavaScript (Arrow Functions).
                    In Python, a specific distinction exists between <strong>Expressions</strong> (something that returns a value) and <strong>Statements</strong> (something that does an action, like `print`, `if`, `while`, or `return`).
                </p>
                <p>
                    A lambda can ONLY contain an expression.
                </p>
                <ul>
                    <li>`x + y` (Expression)</li>
                    <li>`x if x &gt; 0 else 0` (Conditional Expression)</li>
                    <li> `return x` (Statement - Implicitly handled)</li>
                    <li> `x = 5` (Assignment Statement)</li>
                </ul>

                <CodeBlock language="python" code={`# Regular function
def add(x, y):
    return x + y

# Equivalent Lambda
add_lambda = lambda x, y: x + y

print(add(2, 3))        # 5
print(add_lambda(2, 3)) # 5`} />
            </div>

            <div className="content-section">
                <h2>2. The Primary Use Case: Key Functions</h2>
                <p>
                    You rarely assign a lambda to a variable (like `add_lambda` above). PEP 8 discourages it.
                    The <em>real</em> power of lambdas is passing them as arguments to "High Order Functions" like `sorted()`, `min()`, and `max()`.
                </p>

                <h3>Under the Hood: Timsort and Keys</h3>
                <p>
                    Python's default sorting algorithm is <strong>Timsort</strong> (a hybrid of Merge Sort and Insertion Sort).
                    When you provide a `key` argument, Timsort doesn't rearrange the original heavy objects directly during every comparison.
                </p>
                <p>
                    Instead, it runs your lambda function <em>exactly once</em> for each item to create a "shadow list" of keys.
                    It sorts based on these keys and then reassembles the original objects in that order.
                    This is called the "Decorate-Sort-Undecorate" (or Schwartzian Transform) pattern.
                </p>
                <ProTip><p><strong>Efficiency:</strong> Using a lambda key is vastly faster than writing a complex custom comparison function because the lambda only runs N times, not N*log(N) times.</p></ProTip>

                <h3>Sorting Complex Data</h3>
                <p>
                    Imagine you have a list of dictionaries. `sorted()` doesn't know how to sort them.
                    You use a lambda to tell it exactly what value to look at.
                </p>
                <CodeBlock language="python" code={`users = [
    {"name": "Alice", "age": 30},
    {"name": "Bob", "age": 25},
    {"name": "Charlie", "age": 35}
]

# Sort by Age (Ascending)
sorted_by_age = sorted(users, key=lambda u: u["age"])
print(sorted_by_age) 
# [{'name': 'Bob', 'age': 25}, {'name': 'Alice', 'age': 30}, ...]

# Sort by Name length (just for fun)
sorted_by_len = sorted(users, key=lambda u: len(u["name"]))`} />
            </div>

            <div className="content-section">
                <h2>3. The Functional Triad: Map, Filter, Reduce</h2>
                <p>
                    Lambdas effectively enable Functional Programming in Python.
                    They are the engine behind the three most famous functional tools.
                </p>
                <p>
                    <strong>Historical Context:</strong> These concepts aren't unique to Python. They became world-famous due to <strong>Google's MapReduce</strong> paper (2004),
                    which revolutionized Big Data. The idea was simple: split a task into "Mapping" (transforming data in parallel) and "Reducing" (aggregating results).
                    While Python's local `map` and `reduce` don't run on a cluster, they share the same powerful mathematical DNA.
                </p>

                <h3>Filter: Selecting Data</h3>
                <p>`filter(function, iterable)` keeps elements where the function returns `True`.</p>
                <CodeBlock language="python" code={`nums = [1, 2, 3, 4, 5, 6]

# Keep only evens
evens = list(filter(lambda x: x % 2 == 0, nums))
print(evens) # [2, 4, 6]`} />

                <h3>Map: Transforming Data</h3>
                <p>`map(function, iterable)` executes the function on <em>every</em> element.</p>
                <CodeBlock language="python" code={`# Square everyone
squares = list(map(lambda x: x ** 2, nums))
print(squares) # [1, 4, 9, 16, 25, 36]`} />

                <h3>Reduce: Aggregating Data</h3>
                <p>
                    `reduce(function, iterable)` rolls the data up into a single value.
                    Note: In Python 3, this was moved to `functools`.
                </p>
                <CodeBlock language="python" code={`from functools import reduce

# Cumulative Sum: (((1+2)+3)+4)...
total = reduce(lambda acc, x: acc + x, nums)
print(total) # 21`} />
            </div>

            <div className="content-section">
                <h2>The Real Debate: Lambdas vs Comprehensions</h2>
                <ProTip><p>
                        <strong>Pythonic Wisdom:</strong> While `map` and `filter` are cool, most Pythonistas prefer <strong>List Comprehensions</strong>.
                        They are often faster and more readable.
                    </p></ProTip>
                <CodeBlock language="python" code={`# The Map/Filter way
evens = list(filter(lambda x: x % 2 == 0, nums))

# The Comprehension way (Preferred)
evens = [x for x in nums if x % 2 == 0]

# Why? No 'lambda' keyword, no 'list()' wrapping. Just math.`} />
            </div>

            <div className="content-section">
                <h2>4. The Late Binding Trap</h2>
                <ProTip><p>
                        <strong>Stop. Read this.</strong> This bug bites almost everyone who uses lambdas in a loop.
                    </p></ProTip>
                <CodeBlock language="python" code={`#  We want functions that return 0, 1, 2, 3...
functions = [lambda: i for i in range(5)]

# Calling them
print(functions[0]()) # Output: 4
print(functions[1]()) # Output: 4
# THEY ALL RETURN 4! Why?`} />
                <p>
                    <strong>Reason:</strong> Lambdas look up `i` at <em>runtime</em> (lazy evaluation).
                    By the time you call them, the loop has finished, and `i` is 4.
                </p>

                <h3>The Fix: Default Argument Hack</h3>
                <p>
                    To capture the value <em>at definition time</em>, use a default argument.
                </p>
                <CodeBlock language="python" code={`# Capture 'i' as 'x' immediately
functions = [lambda x=i: x for i in range(5)]

print(functions[0]()) # 0 (Correct!)`} />
            </div>

            <div className="content-section">
                <h2>5. Closures with Lambdas</h2>
                <p>
                    You can use lambdas to build quick function factories.
                </p>
                <CodeBlock language="python" code={`def make_adder(n):
    return lambda x: x + n

add_10 = make_adder(10)
add_20 = make_adder(20)

print(add_10(5)) # 15
print(add_20(5)) # 25`} />
            </div>

            <div className="content-section">
                <h2>6. Language Face-off: Python vs JavaScript</h2>
                <p>
                    JavaScript developers love Arrow Functions (`() {"=>"} {"{ }"}`). Python lambdas appear similar but are much more limited.
                </p>
                <ul>
                    <li><strong>JavaScript:</strong> Arrow functions can contain multiple statements, loops, and returns (with `{"{ }"}`).</li>
                    <li><strong>Python:</strong> Lambdas are <strong>strictly single-expression</strong>. No loops, no assignments, no `return` keyword.</li>
                </ul>
                <ProTip><p><strong>Philosophy:</strong> Guido van Rossum (Python's creator) intentionally kept lambdas weak to discourage writing complex "spaghetti code" logic inside one line. If it's complex, give it a name (`def`).</p></ProTip>
            </div>

            <div className="content-section">
                <h2>7. Real World: Callbacks in UI</h2>
                <p>
                    Lambdas are dominant in UI frameworks (like Tkinter, PyQt) or Event-Driven systems.
                    When a button is clicked, you need to run code. You can't call the function immediately; you need to pass a <em>recipe</em> to run later.
                </p>
                <CodeBlock language="python" code={`# Pseudo-code for a UI Framework
def on_click(msg):
    print(f"Button says: {msg}")

#  WRONG: Calls function immediately during setup!
# button.set_command(on_click("Hello")) 

# RIGHT: Passes a lambda that calls it LATER
button.set_command(lambda: on_click("Hello"))`} />
            </div>

            <div className="content-section">
                <h2>8. Functional Deep Dive: Reduce Magic</h2>
                <p>
                    `reduce` is often misunderstood. It is the "Swiss Army Knife" of list processing.
                    Anything `map` or `filter` can do, `reduce` can do (though less readable).
                </p>

                <h3>Flattening a List of Lists</h3>
                <CodeBlock language="python" code={`from functools import reduce

matrix = [[1, 2], [3, 4], [5, 6]]

# Concept: Start with [], then adds [1,2], then [3,4]...
flat = reduce(lambda acc, row: acc + row, matrix, [])
print(flat) # [1, 2, 3, 4, 5, 6]`} />

                <h3>Finding Intersection</h3>
                <CodeBlock language="python" code={`sets = [{1, 2, 3}, {2, 3, 4}, {3, 4, 5}]

# Start with first set, intersect with next...
common = reduce(lambda a, b: a.intersection(b), sets)
print(common) # {3}`} />
            </div>

            <div className="content-section">
                <h2>9. The Dark Side: Limitations & Pitfalls</h2>
                <ProTip><p><strong>Warning:</strong> Lambdas are powerful, but they are not "magic". They act almost exactly like normal functions, with some annoying handicaps.</p></ProTip>

                <h3>Myth: "Lambdas are Faster"</h3>
                <p>
                    A common misconception is that lambdas are faster because they are "smaller". <strong>This is false.</strong>
                    Both `def` and `lambda` compile to the exact same bytecode `MAKE_FUNCTION`.
                    The only difference represents the name lookup mechanism.
                </p>

                <h3>The Pickling Problem (Multiprocessing)</h3>
                <p>
                    This is the #1 reason Data Scientists eventually abandon lambdas in complex pipelines.
                    Python's `multiprocessing` library uses `pickle` to serialize data to send it to other CPU cores.
                </p>
                <p>
                    <strong>How Pickle Works:</strong> It doesn't save the function's <em>code</em>. It saves the <em>name</em> of the function and the module it belongs to (e.g., `my_script.process_data`). When the other CPU receives it, it "imports" that function.
                </p>
                <p>
                    <strong>The Lambda Failure:</strong> Lambdas have no name! Their name is literally `&lt;lambda&gt;`.
                    Because `pickle` cannot look them up in a file, serialization fails with `AttributeError: Can't pickle local object`.
                </p>
                <ul>
                    <li><strong>Use `def`</strong> for anything that needs to run in parallel.</li>
                    <li> <strong>Avoid `lambda`</strong> for heavy data processing pipelines involving `Pool.map`.</li>
                </ul>

                <h3>Other Limitations</h3>
                <ul>
                    <li><strong>Tracebacks:</strong> If a lambda fails, the error says `&lt;lambda&gt;` failed. Good luck finding which one it was in a 1000-line file.</li>
                    <li><strong>Type Hinting:</strong> You can't add type hints (`x: int`) inside a lambda syntax.</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Summary: Best Practices Checklist</h2>
                <ProTip><ul>
                        <li><strong>Don't assign lambdas to names:</strong> `f = lambda x: x+1` is bad. Use `def f(x): return x+1`. It gives better tracebacks.</li>
                        <li><strong>Use for Key Functions:</strong> This is their home. `sorted(data, key=lambda...)`.</li>
                        <li><strong>Prefer Comprehensions:</strong> `[x*2 for x in data]` is better than `map(lambda x: x*2, data)`.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>10. The Better Alternative: Partial Functions</h2>
                <p>
                    Sometimes you use a lambda just to "freeze" an argument.
                    Python has a built-in tool for this that is arguably superior: `functools.partial`.
                </p>
                <ProTip><p><strong>Why Partial?</strong> Unlike lambdas, partial objects <strong>can be pickled</strong>, have a useful `__repr__`, and are slightly faster to call.</p></ProTip>

                <CodeBlock language="python" code={`from functools import partial

def power(base, exponent):
    return base ** exponent

# The Lambda Way
square_lambda = lambda x: power(x, 2)

# The Partial Way
square_partial = partial(power, exponent=2)

print(square_partial(5)) # 25`} />

                <h3>Introspection Comparison</h3>
                <p>
                    See why debugging partials is easier:
                </p>
                <CodeBlock language="python" code={`print(square_lambda)
# <function <lambda> at 0x7f...> (Useless)

print(square_partial)
# functools.partial(<function power at...>, exponent=2) (Clear!)`} />
            </div>

            <div className="content-section">
                <h2>11. Functional Jargon Decoded</h2>
                <p>
                    If you talk to Haskell or Lisp programmers, they use fancy words. Here is what they mean in Python terms:
                </p>
                <ul>
                    <li><strong>First-Class Functions:</strong> Functions are objects. You can pass them, return them, and assign them. Python has this.</li>
                    <li><strong>Higher-Order Function:</strong> A function that takes another function as an arg (e.g., `map`, `sorted`). Python has this.</li>
                    <li><strong>Pure Function:</strong> A function with no side effects (no printing, no global changes). Lambdas encourage this.</li>
                    <li><strong>Currying:</strong> Transforming `f(a, b)` into `f(a)(b)`. Python doesn't do this natively, but you can fake it with lambdas/partials.</li>
                </ul>
            </div>

        </div >
    );
};

export default PythonLambda;



